/**
 * ============================================================================
 * Sessions for a section — the door that was missing
 * ============================================================================
 * Every session screen in this app (the check-in code, the live roster, the
 * presenter, certification, completions) was reachable only by typing a UUID
 * into the address bar. The routes existed, the pages existed, and nothing in
 * the interface linked to any of them — so the whole second half of running a
 * class was invisible to anyone who had not been handed a URL.
 *
 * This is that door. A section lists its sessions, you can schedule one, and
 * each one links to the screens for running it.
 *
 * The QR window is set here rather than being asked for. `defaultQrWindow` on
 * the server already gives ±30 minutes of grace around the scheduled time,
 * deliberately (a window matching the class exactly produces a queue at the
 * door, and the workaround someone invents to clear that queue is what breaks
 * the record). Leaving the fields off the form keeps that default in force.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import ErrorBanner from './ErrorBanner.jsx';
import Field from './Field.jsx';
import Select from './Select.jsx';
import { SessionNextStep, SessionStageBadge } from './SessionNextStep.jsx';

/** "Tue 8 Sep, 18:30" — enough to tell two sessions apart at a glance. */
function when(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function SessionsPanel({ classId, sectionStatus }) {
  const [sessions, setSessions] = useState(undefined);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ scheduled_start: '', scheduled_end: '', location: '', instructor_id: '' });
  /**
   * Who can be named as the instructor. The API always accepted an
   * instructor_id; this form never asked, so every session was owned by
   * whoever scheduled it — the coordinator — and the person actually teaching
   * could not see it, let alone open it. Loaded once the form opens.
   */
  const [staff, setStaff] = useState(undefined);
  /** Session being rescheduled (id) and the new times being typed. */
  const [editing, setEditing] = useState(null);
  const [edit, setEdit] = useState({ scheduled_start: '', scheduled_end: '', location: '' });

  const load = useCallback(async () => {
    try {
      setSessions(await apiFetch(`/api/v1/sessions?class_id=${classId}`));
    } catch (err) {
      setError(err);
      setSessions(null);
    }
  }, [classId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!creating || staff !== undefined) return;
    apiFetch('/api/v1/staff?teaching=1')
      .then((rows) => setStaff(rows ?? []))
      .catch(() => setStaff(null));
  }, [creating, staff]);

  async function create(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // datetime-local gives "2026-09-08T18:30" with no zone. Sending it raw
      // would have the server guess; new Date() resolves it in the browser's
      // own timezone, which is the room's timezone.
      await apiFetch('/api/v1/sessions', {
        method: 'POST',
        body: JSON.stringify({
          class_id: classId,
          scheduled_start: new Date(form.scheduled_start).toISOString(),
          scheduled_end: new Date(form.scheduled_end).toISOString(),
          location: form.location.trim() || null,
          // Blank means "me" — the server defaults to the caller.
          instructor_id: form.instructor_id || null,
        }),
      });
      setForm({ scheduled_start: '', scheduled_end: '', location: '', instructor_id: '' });
      setCreating(false);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  /** "2026-09-22T10:00" in the browser's zone, for a datetime-local input. */
  function toLocalInput(iso) {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  function startEdit(s) {
    setEditing(s.id);
    setEdit({ scheduled_start: toLocalInput(s.scheduled_start), scheduled_end: toLocalInput(s.scheduled_end), location: s.location ?? '' });
  }

  /**
   * Correct the schedule BEFORE the class. The check-in window follows the
   * schedule and cannot be set on its own — that is the audit gate — so the
   * honest way to move the window is to move the class, here, while it is
   * still scheduled.
   */
  async function reschedule(e) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await apiFetch(`/api/v1/sessions/${editing}`, {
        method: 'PATCH',
        body: JSON.stringify({
          scheduled_start: new Date(edit.scheduled_start).toISOString(),
          scheduled_end: new Date(edit.scheduled_end).toISOString(),
          location: edit.location.trim() || null,
        }),
      });
      setEditing(null);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function act(id, path) {
    setError(null);
    setBusy(true);
    try {
      await apiFetch(`/api/v1/sessions/${id}/${path}`, { method: 'POST' });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const published = sectionStatus === 'published';

  return (
    <section className="panel" aria-labelledby="sessions-heading">
      <h2 id="sessions-heading">Sessions</h2>
      <ErrorBanner error={error} />

      <p className="muted">
        A session is one sitting of this section — the thing people actually scan into. Open it
        when the class starts, and the check-in code lives on its roster screen.
      </p>

      {!published && (
        <p className="callout callout--warn">
          <strong>This section is not published yet.</strong>
          <span className="callout__detail">
            Sessions run against a published version, so publish the section first.
          </span>
        </p>
      )}

      {sessions === undefined && (
        <p className="muted" aria-busy="true">
          Loading…
        </p>
      )}

      {sessions && sessions.length === 0 && (
        <p className="muted">No sessions yet for this section.</p>
      )}

      {sessions && sessions.length > 0 && (
        <div className="schedule__scroll">
          <table className="schedule__table">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Where</th>
                <th scope="col">Status</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.id}>
                  <td>{when(s.scheduled_start)}</td>
                  <td>{s.location || <span className="muted">—</span>}</td>
                  <td>
                    <SessionStageBadge session={s} />
                  </td>
                  <td>
                    <SessionNextStep session={s} onOpen={(id) => act(id, 'open')} busy={busy}>
                      {s.status === 'scheduled' && (
                        <button
                          type="button"
                          className="btn btn--quiet btn--auto"
                          disabled={busy}
                          onClick={() => (editing === s.id ? setEditing(null) : startEdit(s))}
                        >
                          {editing === s.id ? 'Cancel change' : 'Change time'}
                        </button>
                      )}
                    </SessionNextStep>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <form onSubmit={reschedule} className="field-row field-row--3">
          <Field label="New start" type="datetime-local" value={edit.scheduled_start} onChange={(v) => setEdit({ ...edit, scheduled_start: v })} />
          <Field label="New end" type="datetime-local" value={edit.scheduled_end} onChange={(v) => setEdit({ ...edit, scheduled_end: v })} />
          <Field label="Location" value={edit.location} onChange={(v) => setEdit({ ...edit, location: v })} required={false} />
          <p className="muted">
            The check-in window follows the schedule (30 minutes either side). It cannot be moved on its
            own — that is the gate.
          </p>
          <div className="schedule__actions">
            <button type="submit" className="btn btn--primary btn--auto" disabled={busy}>
              {busy ? 'Saving…' : 'Save the new time'}
            </button>
          </div>
        </form>
      )}

      {published && !creating && (
        <button type="button" className="btn btn--auto" onClick={() => setCreating(true)}>
          Schedule a session
        </button>
      )}

      {published && creating && (
        <form onSubmit={create} className="field-row field-row--3">
          <Field
            label="Starts"
            type="datetime-local"
            value={form.scheduled_start}
            onChange={(v) => setForm({ ...form, scheduled_start: v })}
          />
          <Field
            label="Ends"
            type="datetime-local"
            value={form.scheduled_end}
            onChange={(v) => setForm({ ...form, scheduled_end: v })}
          />
          <Field
            label="Location"
            value={form.location}
            onChange={(v) => setForm({ ...form, location: v })}
            required={false}
          />
          <Select
            label="Instructor"
            value={form.instructor_id}
            onChange={(v) => setForm({ ...form, instructor_id: v })}
            options={(staff ?? []).map((p) => ({
              value: p.id,
              label: `${p.full_name} — ${p.roles.map((r) => r.name).join(', ') || 'no role'}${
                p.institution?.short_name ? ` (${p.institution.short_name})` : ''
              }`,
            }))}
            placeholder={staff === undefined ? 'Loading…' : staff === null ? 'Could not load staff — it will be you' : 'Me (the person scheduling it)'}
            hint="Who runs the room. They open it, show the code, verify the roster, certify and award. Leave blank to run it yourself."
            required={false}
            disabled={busy}
          />
          <div className="schedule__actions">
            <button type="submit" className="btn btn--primary btn--auto" disabled={busy}>
              {busy ? 'Scheduling…' : 'Schedule it'}
            </button>
            <button
              type="button"
              className="btn btn--quiet btn--auto"
              onClick={() => setCreating(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

    </section>
  );
}

export default SessionsPanel;
